import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import usePlanStore from '../../../../../store/planStore';
import convertDecimalToPercentValue from '../../Plan/Countries/utils/convertDecimalToPercentValue';

interface AccordionSummaryContentProps {
  country: string;
}

function isDdxNullsSentinel(ddx: any) {
  return (
    ddx != null && ddx.type == null && ddx.value == null && ddx.totalDDX == null
  );
}

export function AccordionSummaryContent({
  country,
}: AccordionSummaryContentProps) {
  const { t } = useTranslation();
  const { countryForms, storedCountryForms } = usePlanStore();

  const digitalBudget = countryForms[country].savedCalculatedBudget;
  const offlineBudget = countryForms[country].offline?.tv?.offlineBudget;
  const countryOneReach = countryForms[country].oneReach;

  const sumForDigital = digitalBudget?.reduce(
    (acc, entry) => {
      if (!entry || !entry.budget) return acc;
      return {
        currency: entry.budget.currency,
        value: acc.value + (entry.budget.value ?? 0),
      };
    },
    { currency: digitalBudget?.[0]?.budget?.currency || '', value: 0 }
  ) || { currency: 'PLN', value: 0 };

  const digitalTechCost = digitalBudget?.reduce(
    (acc, entry) => {
      if (!entry?.techCost) return acc;
      return {
        dsp: acc.dsp + (entry.techCost.dsp ?? 0),
        adServer: acc.adServer + (entry.techCost.adServer ?? 0),
      };
    },
    { dsp: 0, adServer: 0 }
  ) || { dsp: 0, adServer: 0 };

  const sumForOffline = offlineBudget
    ?.filter((entry) => entry.touchpoint !== 'tech cost')
    ?.reduce(
      (acc, entry) => {
        if (!entry?.budget) return acc;
        return {
          currency: entry.budget.currency,
          value: acc.value + (entry.budget.value ?? 0),
        };
      },
      { currency: offlineBudget?.[0]?.budget?.currency || '', value: 0 }
    ) || { currency: '', value: 0 };

  const offlineTechCost = offlineBudget
    ?.filter((entry) => entry.touchpoint === 'tech cost')
    .map((entry) => ({
      value: entry.budget.value,
      currency: entry.budget.currency,
    })) || [{ value: 0, currency: 'PLN' }];

  const getMetaRFAvailability = (targetCountry: string) => {
    return storedCountryForms[targetCountry].availableTouchpoints?.some(
      (touchpoint) =>
        touchpoint.TouchpointName === 'META' &&
        touchpoint.touchpointFunnel.toLowerCase().includes('awareness')
    );
  };

  const { ddx } = storedCountryForms[country];
  const ddxNotApplicable = isDdxNullsSentinel(ddx);

  const getDDXValue = () => {
    if (ddxNotApplicable) return 'N/A';

    switch (ddx?.type) {
      case 'percentage':
        return `${formatNumberWithSpaces(
          (
            (sumForDigital.value + sumForOffline.value) *
            (ddx?.value ?? 0)
          ).toFixed(0)
        )}${sumForDigital.currency}`;
      case 'fixed':
        return `${formatNumberWithSpaces((ddx?.value ?? 0).toFixed(0))}${
          sumForDigital.currency
        }`;
      case 'omitted':
        return 'N/A';
      default:
        return 'N/A';
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body1" fontWeight={600} width="110px">
          {country}
        </Typography>

        <Typography variant="caption" textTransform="uppercase" ml={4}>
          {t('mediaPlanning.oneReach')}:{' '}
          {country !== 'POLAND' || !getMetaRFAvailability(country)
            ? 'N/A'
            : `${convertDecimalToPercentValue(countryOneReach)}%`}
        </Typography>

        {ddx?.type && (
          <Typography variant="caption" textTransform="uppercase" ml={4}>
            DDX: {getDDXValue()}
          </Typography>
        )}
      </Box>

      {getMetaRFAvailability(country) && (
        <Box display="flex">
          <Typography
            variant="caption"
            textTransform="uppercase"
            lineHeight={2}
            mr={2}
          >
            Meta status:
          </Typography>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              textTransform="uppercase"
              fontWeight={700}
            >
              RF Main:
            </Typography>
            <Stack sx={{ flexDirection: 'row' }}>
              {storedCountryForms[country].metaConfig?.reservationStatus ===
                'reserved' ||
              storedCountryForms[country].metaConfig?.reservationStatus ===
                'partially-reserved' ? (
                <DoneIcon sx={{ color: '#439A37' }} />
              ) : (
                <CloseIcon sx={{ color: '#F00F00' }} />
              )}
            </Stack>
            {storedCountryForms[country].metaConfig
              ?.isMetaPartialPredictionEnabled ? (
              <>
                <Typography
                  variant="caption"
                  textTransform="uppercase"
                  fontWeight={700}
                >
                  RF 1P:
                </Typography>
                <Stack>
                  {storedCountryForms[country].metaConfig?.reservationStatus ===
                  'reserved' ? (
                    <DoneIcon sx={{ color: '#439A37' }} />
                  ) : (
                    <CloseIcon sx={{ color: '#F00F00' }} />
                  )}
                </Stack>
              </>
            ) : (
              <Box width="62px" />
            )}
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '246px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" textTransform="uppercase">
            {`${t('mediaPlanning.totalBudget')}: `}
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {formatNumberWithSpaces(
              (sumForDigital.value + sumForOffline.value).toFixed(0)
            )}{' '}
            {sumForDigital.currency ?? sumForOffline.currency}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" textTransform="uppercase">
            {`${t('mediaPlanning.techCosts')}: `}
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {formatNumberWithSpaces(
              (
                digitalTechCost.dsp +
                digitalTechCost.adServer +
                offlineTechCost[0].value
              ).toFixed(0)
            )}{' '}
            {offlineTechCost[0].currency ?? 'PLN'}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
